import React, { useState } from 'react';

import { timingBridgeAPI } from '../../../lib/api';

const BUTTON = 'px-3 py-1.5 text-xs rounded border border-[#2a2a2a] bg-[#0a0a0a] text-gray-300 hover:border-[#3a3a3a] disabled:opacity-30';

function ago(iso) {
  if (!iso) return '';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  return s < 60 ? `${s}s ago` : `${Math.round(s / 60)} min ago`;
}

/**
 * Rider photos on the Live screen: does each championship's top five have a
 * picture, and which files in the folder did not match anyone cleanly. The
 * point is to find a missing or misnamed photo before the champion strap goes
 * to air, not after.
 */
export default function PhotosPanel({ showId, photos, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  if (!photos) return null;
  const { folder, fileCount, loadedAt, loading, championships = [], unmatched = [], ambiguous = [], mismatched = [], duplicates = [] } = photos;
  const problems = mismatched.length + ambiguous.length + unmatched.length + duplicates.length;

  async function reload(nextFolder) {
    setBusy(true);
    setError('');
    try {
      await timingBridgeAPI.reloadPhotos(showId, nextFolder);
      setEditing(false);
      await onChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not read the photos folder');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-lg border border-[#2a2a2a] bg-[#111111] p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-white">Rider photos</h3>
        <span className="text-[11px] text-gray-500">
          {!folder ? 'No folder set' : loading ? 'Reading the folder…' : `${fileCount} picture${fileCount === 1 ? '' : 's'}${loadedAt ? `, read ${ago(loadedAt)}` : ''}`}
        </span>
        <div className="ml-auto flex gap-2">
          {folder && <button className={BUTTON} disabled={busy} onClick={() => reload()}>{busy ? 'Reading…' : 'Re-read now'}</button>}
          <button className={BUTTON} disabled={busy} onClick={() => { setDraft(folder || ''); setEditing(e => !e); }}>{folder ? 'Change folder' : 'Add a folder'}</button>
        </div>
      </div>

      {editing && (
        <div className="flex gap-2">
          <input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="Google Drive folder link"
            className="flex-1 bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500"
          />
          <button className={BUTTON} disabled={busy} onClick={() => reload(draft.trim())}>Use this folder</button>
        </div>
      )}

      {(photos.error || error) && (
        <p className="text-[11px] text-amber-500">
          {error || photos.error}
          {photos.shareWith && !String(error || photos.error).includes(photos.shareWith) && <> Share it with <code className="select-all text-amber-300">{photos.shareWith}</code>.</>}
        </p>
      )}

      {folder && championships.map(c => (
        <div key={c.key}>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">{c.label}: top {c.riders.length}</div>
          <div className="flex flex-wrap gap-2">
            {c.riders.map(r => (
              <div key={r.no} className={`flex items-center gap-2 px-2 py-1 rounded border text-xs ${r.photo ? 'border-[#2a2a2a] bg-[#0a0a0a]' : 'border-amber-800 bg-amber-950/30'}`} title={r.file ? `${r.file} (matched by ${r.via})` : 'No picture in the folder for this rider'}>
                {r.photo
                  ? <img src={r.photo} alt="" className="w-7 h-7 rounded object-cover bg-[#1a1a1a]" />
                  : <span className="w-7 h-7 rounded bg-[#1a1a1a] flex items-center justify-center text-amber-500">?</span>}
                <span className="text-gray-500 tabular-nums">P{r.pos}</span>
                <span className="text-gray-200">#{r.no} {r.name}</span>
                {!r.photo && <span className="text-amber-500">no photo</span>}
              </div>
            ))}
          </div>
        </div>
      ))}

      {folder && problems > 0 && (
        <div className="space-y-1 text-[11px]">
          {mismatched.map(m => (
            <p key={m.file} className="text-amber-400">{m.file}: #{m.no} is {m.rider}. Used for #{m.no}; rename it if that is the wrong rider.</p>
          ))}
          {ambiguous.map(a => (
            <p key={a.file} className="text-amber-400">{a.file}: could be {a.riders.join(' or ')}. Not used; add the race number to the name.</p>
          ))}
          {duplicates.map(d => (
            <p key={d.ignored} className="text-gray-500">{d.ignored}: another picture for #{d.no}; using {d.kept}.</p>
          ))}
          {unmatched.length > 0 && (
            <p className="text-gray-500">Matched nobody: {unmatched.map(u => u.file).join(', ')}</p>
          )}
        </div>
      )}
    </section>
  );
}
